import React, { createContext, useContext, useCallback, useMemo, ReactNode } from 'react';

// Define the structure for different page configs
export interface ManageModelsConfig {
  systemBrowserState: {
    expandedSystems: number[];
  };
  lastVisitedModel?: {
    tagId: string;
    systemId: number;
  };
}

export interface AlertBoardConfig {
  systemBrowserState: {
    expandedSystems: number[];
  };
}

export interface AppConfig {
  manageModels: ManageModelsConfig;
  floatingTagExplorer: ManageModelsConfig;
  alertBoard: AlertBoardConfig;
  // Add other page configs here as needed
  // manageAlerts: ManageAlertsConfig;
  // manageLimits: ManageLimitsConfig;
}

interface AppConfigContextType {
  config: AppConfig;
  updateConfig: <K extends keyof AppConfig>(page: K, updates: Partial<AppConfig[K]>) => void;
  getPageConfig: <K extends keyof AppConfig>(page: K) => AppConfig[K];
}

const defaultConfig: AppConfig = {
  manageModels: {
    systemBrowserState: {
      expandedSystems: [],
    },
  },
  floatingTagExplorer: {
    systemBrowserState: {
      expandedSystems: [],
    },
  },
  alertBoard: {
    systemBrowserState: {
      expandedSystems: [],
    },
  },
};

const AppConfigContext = createContext<AppConfigContextType | undefined>(undefined);

interface AppConfigProviderProps {
  children: ReactNode;
}

export const AppConfigProvider: React.FC<AppConfigProviderProps> = ({ children }) => {
  // Load config from sessionStorage on mount
  const loadConfig = useCallback((): AppConfig => {
    try {
      const stored = sessionStorage.getItem('app-config');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with default config to ensure all required properties exist
        return {
          manageModels: {
            ...defaultConfig.manageModels,
            ...parsed.manageModels,
          },
          floatingTagExplorer: {
            ...defaultConfig.floatingTagExplorer,
            ...parsed.floatingTagExplorer,
          },
          alertBoard: {
            ...defaultConfig.alertBoard,
            ...parsed.alertBoard,
          },
        };
      }
    } catch (error) {
      console.warn('Failed to load app config from sessionStorage:', error);
    }
    return defaultConfig;
  }, []);

  // Save config to sessionStorage
  const saveConfig = useCallback((config: AppConfig) => {
    try {
      sessionStorage.setItem('app-config', JSON.stringify(config));
    } catch (error) {
      console.warn('Failed to save app config to sessionStorage:', error);
    }
  }, []);

  const [config, setConfig] = React.useState<AppConfig>(loadConfig);

  // Update config for a specific page
  const updateConfig = useCallback(
    <K extends keyof AppConfig>(page: K, updates: Partial<AppConfig[K]>) => {
      setConfig((prev) => {
        const newConfig = {
          ...prev,
          [page]: {
            ...prev[page],
            ...updates,
          },
        };
        saveConfig(newConfig);
        return newConfig;
      });
    },
    [saveConfig],
  );

  // Get config for a specific page
  const getPageConfig = useCallback(
    <K extends keyof AppConfig>(page: K): AppConfig[K] => {
      return config[page];
    },
    [config],
  );

  const contextValue = useMemo(
    () => ({
      config,
      updateConfig,
      getPageConfig,
    }),
    [config, updateConfig, getPageConfig],
  );

  return <AppConfigContext.Provider value={contextValue}>{children}</AppConfigContext.Provider>;
};

export const useAppConfig = (): AppConfigContextType => {
  const context = useContext(AppConfigContext);
  if (context === undefined) {
    throw new Error('useAppConfig must be used within an AppConfigProvider');
  }
  return context;
};

// Hook for specific page configs
